import produce from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { ControlUnitAPIResponse } from '../../../../../../redux/controlUnit/api/ControlUnit.model';
import {
  CHANNELS_CONNECTIONS_SUPPORTED,
  getConnectionFilterLogicalObjectFromZipDeviceControlUnits,
} from './useLabStateFilters';
import { get, memoize, isEqual, setWith } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import {
  CommanderMonitoringFilterLogicalObject,
  ControlUnitChannelFilterLogicalObject,
} from '../../../../../../redux/monitoring/api/util/filter/filter.model';
import { ZipDeviceControlUnit } from '../../../../../../redux/controlUnit/controlUnit.model';

export type STATUS_FILTER_CHANNEL_OR_EVENT =
  | 'checked'
  | 'indeterminate'
  | 'none';
export const useConnectionControls = (
  type: 'labConnection',
  zipDevicesControlUnits: ZipDeviceControlUnit[],
  defaultValue?: CommanderMonitoringFilterLogicalObject
) => {
  const defaultFilterObject = useMemo(
    () =>
      getConnectionFilterLogicalObjectFromZipDeviceControlUnits(
        zipDevicesControlUnits,
        true
      ),
    [zipDevicesControlUnits, type]
  );
  const [filterLogicalObject, setFilterLogicalObject] =
    useState<CommanderMonitoringFilterLogicalObject>(
      defaultValue ?? defaultFilterObject
    );

  const reset = () => {
    setFilterLogicalObject(defaultValue ?? defaultFilterObject);
  };
  const getStateItem = (
    deviceId: string,
    controlUnitId: string,
    channelOrEventId: string
  ): STATUS_FILTER_CHANNEL_OR_EVENT => {
    const path = [deviceId, controlUnitId, channelOrEventId];
    const value = get(filterLogicalObject, path);
    return value ? 'checked' : 'none';
  };

  const toggleItem = useCallback(
    (deviceId: string, controlUnitId: string, channelOrEventId: string) => {
      setFilterLogicalObject(
        produce((draftFilterLogicalObject) => {
          setChannelOrEventId(
            draftFilterLogicalObject,
            deviceId,
            controlUnitId,
            channelOrEventId
          );
        })
      );
    },
    [setFilterLogicalObject]
  );
  const getStateControlUnit: (
    deviceId: string,
    controlUnitId: string
    // FIXME: react-hooks plugin not working, eslint-disable-next-line react-hooks/exhaustive-deps
  ) => STATUS_FILTER_CHANNEL_OR_EVENT = useCallback(
    memoize(
      (deviceId: string, controlUnitId: string) => {
        const path = [deviceId, controlUnitId];
        const eventsOrChannelsOnFilter = get(
          filterLogicalObject,
          path
        ) as ControlUnitChannelFilterLogicalObject;

        if (
          !eventsOrChannelsOnFilter ||
          Object.values(eventsOrChannelsOnFilter ?? {}).every(
            (item) => item === false
          )
        )
          return 'none';

        const eventsOrChannelsOnControlUnit = get(
          defaultFilterObject,
          path
        ) as ControlUnitChannelFilterLogicalObject;
        const hasAllChecked = isEqual(
          eventsOrChannelsOnControlUnit,
          eventsOrChannelsOnFilter
        );
        return hasAllChecked ? 'checked' : 'indeterminate';
      },
      (...args: any[]) => args.join('-')
    ),
    [filterLogicalObject, type]
  );
  const toggleControlUnit = useCallback(
    (deviceId: string, controlUnit: ControlUnitAPIResponse): void => {
      setFilterLogicalObject(
        produce((draftFilterLogicalObject) => {
          const newValue =
            getStateControlUnit(deviceId, controlUnit.id) === 'checked'
              ? false
              : true;
          setControlUnitLabConnection(
            draftFilterLogicalObject,
            deviceId,
            controlUnit,
            type,
            newValue
          );
        })
      );
    },
    [getStateControlUnit, type]
  );

  const getStateDevice: (
    zipDeviceControlUnit: ZipDeviceControlUnit
    // FIXME: react-hooks plugin not working, eslint-disable-next-line react-hooks/exhaustive-deps
  ) => STATUS_FILTER_CHANNEL_OR_EVENT = useCallback(
    memoize((zipDeviceControlUnit: ZipDeviceControlUnit) => {
      const path = [zipDeviceControlUnit.deviceId];
      const eventsOrChannelsOnFilter = get(
        filterLogicalObject,
        path
      ) as ControlUnitChannelFilterLogicalObject;

      if (
        !eventsOrChannelsOnFilter ||
        Object.values(eventsOrChannelsOnFilter ?? {})
          .flatMap((item) => Object.values(item ?? {}))
          .every((item) => item === false)
      )
        return 'none';

      const eventsOrChannelsOnControlUnit = get(
        defaultFilterObject,
        path
      ) as ControlUnitChannelFilterLogicalObject;
      const hasAllChecked = isEqual(
        eventsOrChannelsOnControlUnit,
        eventsOrChannelsOnFilter
      );
      return hasAllChecked ? 'checked' : 'indeterminate';
    }),
    [filterLogicalObject, type]
  );
  const toggleDevice = useCallback(
    (zipDeviceControlUnit: ZipDeviceControlUnit): void => {
      setFilterLogicalObject(
        produce((draftFilterLogicalObject) => {
          const newValue =
            getStateDevice(zipDeviceControlUnit) === 'checked' ? false : true;
          const deviceId = zipDeviceControlUnit.deviceId;
          Object.entries(draftFilterLogicalObject).forEach(
            ([inDeviceId, deviceFilterObject]) => {
              if (inDeviceId === deviceId && deviceFilterObject) {
                Object.values(deviceFilterObject).forEach(
                  (controlUnitFilterObject) => {
                    controlUnitFilterObject &&
                      Object.keys(controlUnitFilterObject).forEach(
                        (channelOrEventId) => {
                          controlUnitFilterObject[channelOrEventId] = newValue;
                        }
                      );
                  }
                );
              }
            }
          );
        })
      );
    },
    [getStateDevice, type]
  );
  return {
    filterLogicalObject,
    toggleItem,
    toggleControlUnit,
    toggleDevice,
    getStateItem,
    getStateControlUnit,
    getStateDevice,
    reset,
  };
};

export function setChannelOrEventId(
  draftFilterLogicalObject: WritableDraft<CommanderMonitoringFilterLogicalObject>,
  deviceId: string,
  controlUnitId: string,
  channelOrEventId: string,
  value?: boolean
) {
  const path = [deviceId, controlUnitId, channelOrEventId];
  const newValue = value ?? !get(draftFilterLogicalObject, path);
  setWith(draftFilterLogicalObject, path, newValue, Object);
}

export function setControlUnitLabConnection(
  draftFilterLogicalObject: WritableDraft<CommanderMonitoringFilterLogicalObject>,
  deviceId: string,
  controlUnit: ControlUnitAPIResponse,
  type: 'labConnection',
  value?: boolean
) {
  const channelOrEventIds = CHANNELS_CONNECTIONS_SUPPORTED;
  channelOrEventIds.forEach((channelOrEventId) => {
    setChannelOrEventId(
      draftFilterLogicalObject,
      deviceId,
      controlUnit.id,
      channelOrEventId,
      value
    );
  });
}
